import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, GripHorizontal, PanelRight } from "lucide-react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const MARGIN = 8;
const TOPBAR = 8;

// Draggable + resizable floating window (portaled to body), TunerPro-style.
// Pointer-events based so it works with touch (Android tablet) as well as mouse,
// and is clamped inside the viewport so it never gets cut off / dragged off-screen.
export function FloatingWindow({ title, onClose, initial = {}, children, testid, accent = "text-cyan-300", onDock }) {
  const fitSize = () => {
    const w = clamp(initial.w ?? 720, 280, Math.max(280, window.innerWidth - MARGIN * 2));
    const h = clamp(initial.h ?? 500, 200, Math.max(200, window.innerHeight - MARGIN - 56));
    return { w, h };
  };
  const fitPos = (size) => ({
    x: clamp(initial.x ?? 360, MARGIN, Math.max(MARGIN, window.innerWidth - size.w - MARGIN)),
    y: clamp(initial.y ?? 150, TOPBAR, Math.max(TOPBAR, window.innerHeight - size.h - MARGIN)),
  });

  const [size, setSize] = useState(fitSize);
  const [pos, setPos] = useState(() => fitPos(fitSize()));
  const [docked, setDocked] = useState(false);
  const dragRef = useRef(null);
  const rzRef = useRef(null);
  const dockedRef = useRef(false);
  const savedRef = useRef(null);

  // Tiled/docked geometry: snap to the right side of the viewport, beside the map.
  const dockGeom = () => {
    const w = Math.max(320, Math.floor(window.innerWidth * 0.46));
    const h = Math.max(200, window.innerHeight - TOPBAR - MARGIN);
    return { x: Math.max(MARGIN, window.innerWidth - w - MARGIN), y: TOPBAR, w, h };
  };
  const toggleDock = () => {
    if (!dockedRef.current) {
      savedRef.current = { pos, size };
      const d = dockGeom();
      setPos({ x: d.x, y: d.y }); setSize({ w: d.w, h: d.h });
      dockedRef.current = true; setDocked(true);
    } else {
      const s = savedRef.current;
      dockedRef.current = false; setDocked(false);
      if (s) { setSize(s.size); setPos(s.pos); }
    }
  };

  // Re-clamp on viewport change (device rotate / resize) so it stays fully visible.
  useEffect(() => {
    const onResize = () => {
      if (dockedRef.current) {
        const d = dockGeom();
        setSize({ w: d.w, h: d.h }); setPos({ x: d.x, y: d.y });
        return;
      }
      setSize((s) => {
        const w = clamp(s.w, 280, Math.max(280, window.innerWidth - MARGIN * 2));
        const h = clamp(s.h, 200, Math.max(200, window.innerHeight - MARGIN - 56));
        setPos((p) => ({
          x: clamp(p.x, MARGIN, Math.max(MARGIN, window.innerWidth - w - MARGIN)),
          y: clamp(p.y, TOPBAR, Math.max(TOPBAR, window.innerHeight - h - MARGIN)),
        }));
        return { w, h };
      });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const startDrag = (e) => {
    e.preventDefault();
    if (dockedRef.current) { dockedRef.current = false; setDocked(false); }
    dragRef.current = { mx: e.clientX, my: e.clientY, x: pos.x, y: pos.y };
    const move = (ev) => {
      if (!dragRef.current) return;
      const maxX = Math.max(MARGIN, window.innerWidth - size.w - MARGIN);
      const maxY = Math.max(TOPBAR, window.innerHeight - size.h - MARGIN);
      setPos({
        x: clamp(dragRef.current.x + ev.clientX - dragRef.current.mx, MARGIN, maxX),
        y: clamp(dragRef.current.y + ev.clientY - dragRef.current.my, TOPBAR, maxY),
      });
    };
    const up = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const startResize = (e, dir = "se") => {
    e.preventDefault(); e.stopPropagation();
    if (dockedRef.current) { dockedRef.current = false; setDocked(false); }
    rzRef.current = { mx: e.clientX, my: e.clientY, w: size.w, h: size.h };
    const move = (ev) => {
      if (!rzRef.current) return;
      const maxW = window.innerWidth - pos.x - MARGIN;
      const maxH = window.innerHeight - pos.y - MARGIN;
      setSize({
        w: dir.includes("e") ? clamp(rzRef.current.w + ev.clientX - rzRef.current.mx, 280, maxW) : rzRef.current.w,
        h: dir.includes("s") ? clamp(rzRef.current.h + ev.clientY - rzRef.current.my, 200, maxH) : rzRef.current.h,
      });
    };
    const up = () => { rzRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  return createPortal(
    <div data-testid={testid} className="fixed z-[60] rounded-xl border border-slate-700 bg-[#0c1017] shadow-2xl shadow-black/60 flex flex-col overflow-hidden max-w-[100vw] max-h-[100vh]"
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}>
      <div onPointerDown={startDrag} style={{ touchAction: "none" }} className="flex items-center gap-2 px-3 py-2 bg-[#151b28] border-b border-slate-700 cursor-move select-none">
        <GripHorizontal className="h-4 w-4 text-slate-500 shrink-0" />
        <span className={`font-head font-bold uppercase text-sm truncate ${accent}`}>{title}</span>
        <button data-testid={`${testid}-dock`} onClick={onDock || toggleDock} onPointerDown={(e) => e.stopPropagation()} title={onDock ? "Dock beside map" : (docked ? "Undock (float)" : "Tile beside map")} className={`ml-auto p-1 rounded hover:bg-white/10 shrink-0 ${docked ? "text-emerald-400" : "text-slate-400 hover:text-white"}`}><PanelRight className="h-4 w-4" /></button>
        <button data-testid={`${testid}-close`} onClick={onClose} onPointerDown={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white shrink-0"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 min-h-0 p-3 overflow-hidden flex flex-col">{children}</div>
      {/* Right edge — drag to resize width */}
      <div onPointerDown={(e) => startResize(e, "e")} style={{ touchAction: "none" }} data-testid={`${testid}-resize-e`}
        className="absolute top-8 bottom-8 right-0 w-3 cursor-ew-resize hover:bg-red-500/30 active:bg-red-500/50" />
      {/* Bottom edge — drag to resize height (naik/turun) */}
      <div onPointerDown={(e) => startResize(e, "s")} style={{ touchAction: "none" }} data-testid={`${testid}-resize-s`}
        className="absolute left-8 right-8 bottom-0 h-3 cursor-ns-resize hover:bg-red-500/30 active:bg-red-500/50" />
      {/* Bottom-right corner — drag to resize both */}
      <div onPointerDown={(e) => startResize(e, "se")} style={{ touchAction: "none" }} data-testid={`${testid}-resize`}
        className="absolute bottom-0 right-0 w-9 h-9 cursor-nwse-resize flex items-end justify-end p-1 text-slate-400 hover:text-red-400">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" />
        </svg>
      </div>
    </div>,
    document.body
  );
}
